import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Edit, Trash2, ArrowRight, Star, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { adminGetAllProjects, adminCreateProject, adminUpdateProject, adminDeleteProject } from '../api/api';

export const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    service_type: '',
    before_image: '',
    after_image: '',
    location: '',
    is_featured: false,
    is_visible: true
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const data = await adminGetAllProjects();
      setProjects(data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (project = null) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        title: project.title,
        description: project.description,
        service_type: project.service_type,
        before_image: project.before_image,
        after_image: project.after_image,
        location: project.location || '',
        is_featured: project.is_featured,
        is_visible: project.is_visible
      });
    } else {
      setEditingProject(null);
      setFormData({
        title: '',
        description: '',
        service_type: '',
        before_image: '',
        after_image: '',
        location: '',
        is_featured: false,
        is_visible: true
      });
    }
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingProject(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProject) {
        await adminUpdateProject(editingProject.id, formData);
        toast.success('Projet mis à jour');
      } else {
        await adminCreateProject(formData);
        toast.success('Projet créé');
      }
      
      handleCloseDialog();
      fetchProjects();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce projet ?')) return;

    try {
      await adminDeleteProject(id);
      toast.success('Projet supprimé');
      fetchProjects();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gestion des projets</h2>
          <p className="text-gray-600">Galerie avant/après de vos réalisations</p>
        </div>
        <Button onClick={() => handleOpenDialog()} className="bg-blue-600">
          <Plus className="w-4 h-4 mr-2" />
          Nouveau projet
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {projects.map((project) => (
          <Card key={project.id} className={`border-0 shadow-lg ${!project.is_visible ? 'opacity-50' : ''}`}>
            <div className="relative h-48 overflow-hidden rounded-t-xl">
              <div className="absolute inset-y-0 left-0 w-1/2 overflow-hidden">
                <img src={project.before_image} alt="Avant" className="w-full h-full object-cover" />
                <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-semibold">Avant</div>
              </div>
              <div className="absolute inset-y-0 right-0 w-1/2 overflow-hidden">
                <img src={project.after_image} alt="Après" className="w-full h-full object-cover" />
                <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">Après</div>
              </div>
              <div className="absolute inset-y-0 left-1/2 w-1 bg-white shadow-lg transform -translate-x-1/2 z-10">
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-full p-1.5 shadow-xl">
                  <ArrowRight className="w-4 h-4 text-blue-600" />
                </div>
              </div>
              {project.is_featured && (
                <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-20 bg-yellow-400 text-yellow-900 px-2 py-1 rounded text-xs font-semibold flex items-center">
                  <Star className="w-3 h-3 mr-1" />
                  Vedette
                </div>
              )}
              <div className="absolute bottom-2 right-2 flex space-x-2 z-20">
                <button onClick={() => handleOpenDialog(project)} className="p-2 bg-white rounded-lg hover:bg-blue-50 shadow-lg">
                  <Edit className="w-4 h-4 text-blue-600" />
                </button>
                <button onClick={() => handleDelete(project.id)} className="p-2 bg-white rounded-lg hover:bg-red-50 shadow-lg">
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
            </div>

            <CardContent className="p-6">
              <h3 className="font-bold text-lg text-gray-900 mb-2">{project.title}</h3>
              <p className="text-gray-600 text-sm mb-3">{project.description}</p>
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">{project.service_type}</span>
                {project.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" aria-hidden="true" /> {project.location}</span>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProject ? 'Modifier le projet' : 'Nouveau projet'}</DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titre *</Label>
              <Input id="title" value={formData.title} onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))} required />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea id="description" value={formData.description} onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} required rows={3} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="service_type">Type de service *</Label>
                <Input id="service_type" value={formData.service_type} onChange={(e) => setFormData(prev => ({ ...prev, service_type: e.target.value }))} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="location">Localisation</Label>
                <Input id="location" value={formData.location} onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))} placeholder="Liège" />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="before_image">Image AVANT *</Label>
              <Input id="before_image" type="url" value={formData.before_image} onChange={(e) => setFormData(prev => ({ ...prev, before_image: e.target.value }))} placeholder="https://..." required />
              {formData.before_image && <img src={formData.before_image} alt="Preview avant" className="w-full h-32 object-cover rounded-lg mt-2" />}
            </div>

            <div className="space-y-2">
              <Label htmlFor="after_image">Image APRÈS *</Label>
              <Input id="after_image" type="url" value={formData.after_image} onChange={(e) => setFormData(prev => ({ ...prev, after_image: e.target.value }))} placeholder="https://..." required />
              {formData.after_image && <img src={formData.after_image} alt="Preview après" className="w-full h-32 object-cover rounded-lg mt-2" />}
            </div>

            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Switch id="featured" checked={formData.is_featured} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_featured: checked }))} />
                <Label htmlFor="featured">Projet vedette</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch id="visible" checked={formData.is_visible} onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_visible: checked }))} />
                <Label htmlFor="visible">Visible sur le site</Label>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={handleCloseDialog}>Annuler</Button>
              <Button type="submit" className="bg-blue-600">{editingProject ? 'Mettre à jour' : 'Créer'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
